import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import Button from "@React_UI_Lib/Button";
import Tooltip from "@React_UI_Lib/ReactToolTip";
import DateAndTime from "../HOBTimeWidget/HOBTimeWidget";
import { useToast } from "../../Toast";
import ErrorScreen from "../ErrorScreen/ErrorScreen";
import * as S from "./HouseOfBrandsStyles";
import {
  GOIBIBO,
  GOIBIBO_ICON_URL,
  HOB_CAR_IMG_URL,
  HOB_CHAT_ICON_URL,
  HOB_DOWN_ARROW_URL,
  HOB_EDIT_FILL_ICON,
  HOB_FROM_IMG_URL,
  HOB_FROM_IMG_URL_DISABLED,
  HOB_TO_IMG_URL,
  HOB_TO_IMG_URL_DISABLED,
  PROVISIONAL_HOB_REQUEST_TYPE,
  errActionCta,
  errIcon,
  smallCabLogos,
} from "../../../../../config/constants";
import { getProvisionalDataHOB } from "../../../redux/thunks";
import SrcDestPicker from "../SrcDestPicker/SrcDestPicker";
import {
  hobDestinationLocationSelector,
  hobFlightIdSelector,
  hobIsDepartureSelector,
  hobLandingResponseSelector,
  hobPickupTimeSelector,
  hobSourceLocationSelector,
  hobSourceSelector,
  useSourceLocationSelector,
} from "../../../redux/selector";
import {
  UPDATE_HOB_LANDING_RESPONSE,
  UPDATE_HOB_PICKUP_TIME,
  UPDATE_USE_SOURCE_LOCATION,
} from "../../../redux/action-types";
import { getRandomNumber, formatTime, formatDate } from "../../../utils";
import useNavigate from "../../../../../hooks/useNavigate";
import {
  gcHobLandingScreen,
  pageViewOmniEvent,
  pushCtaOmniEvents,
  sendEventOnError,
} from "../../../../../config/omni";
import {
  CABS_HOB_LANDING_ROUTE,
  CALENDAR_BACK_CLICKED,
  CALENDAR_DONE_CLICKED,
  CHECK_FARE_CLICKED,
  DATE_EDIT_CLICKED,
  DESTINATION_NOT_ENTERED,
  DYNAMIC_ERROR,
  EDIT_SOURCE_CLICKED,
  HOB_LANDING_PAGE,
  MOB_CABS_HOB_LANDING,
  SOURCE_DESTINATION_SAME,
  SOURCE_NOT_ENTERED,
} from "../../../../../utils/util-constants";
import { CTA_TYPE } from "../../../../../utils/omni-utils";
import { DateTimePicker } from "@gommt-cabs/ui-lib";

const HouseOfBrands = (props) => {
  const showToast = useToast();
  const dispatch = useDispatch();
  const store = useStore();
  const [navigateTo] = useNavigate();

  const [inputValue, setInputValue] = useState("Enter drop location ");
  const [sourceInput, setSourceInput] = useState("Enter pick up location ");
  const [cabPosition, setCabPosition] = useState("");
  const [errScreenData, setErrScreenData] = useState(null);
  const [provResponse, setProvisionalResponse] = useState(null);
  const [showDateTimeWidget, setShowDateTimeWidget] = useState(false);
  const [showDestPickerWidget, setShowDestPickerWidget] = useState(false);
  const [showSrcPickerWidget, setShowSrcPickerWidget] = useState(false);
  const [minimumPickupDate, setMinimumPickupDate] = useState(new Date());
  const [randomNumber, setRandomNumber] = useState(getRandomNumber());

  const flightId = useSelector(hobFlightIdSelector);
  const source = useSelector(hobSourceSelector);
  const isDeparture = useSelector(hobIsDepartureSelector);
  const provisionalResponse = useSelector(hobLandingResponseSelector);
  const time = useSelector(hobPickupTimeSelector);
  const destinationLocation = useSelector(hobDestinationLocationSelector);
  const sourceLocation = useSelector(hobSourceLocationSelector);
  const useSourceLocation = useSelector(useSourceLocationSelector);

  useEffect(() => {
    async function fetchAndSetProvisonalData(request) {
      const response = await getProvisionalDataHOB(request);
      const provisionalResponse = response?.response?.[0] || null;
      setProvisionalResponse(provisionalResponse);
      if (provisionalResponse) {
        dispatch({
          type: UPDATE_HOB_LANDING_RESPONSE,
          data: {
            provisionalResponse: provisionalResponse,
            source_message: provisionalResponse?.source_message,
            airport_city_code: provisionalResponse?.airport_city_code,
            terminal: provisionalResponse?.terminal,
            pickupTime: provisionalResponse?.default_pickup_time,
          },
        });
      }
      validateAndTrackeProvisionalResponse(response);
    }
    let animationFrameId;
    let lastUpdate = Date.now();

    const animate = () => {
      const now = Date.now();
      if (now - lastUpdate >= 5000) {
        // 5000 ms = 5 seconds
        setCabPosition((prevCabPosition) =>
          prevCabPosition === "newCabPosition" ? "" : "newCabPosition"
        );
        const newRandomNumber = getRandomNumber();
        setRandomNumber(newRandomNumber);
        lastUpdate = now;
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    try {
      let request = {
        flight_id: flightId,
        source_param: source,
        is_departure: isDeparture,
        type: PROVISIONAL_HOB_REQUEST_TYPE,
      };
      if (Boolean(provisionalResponse)) {
        pageViewOmniEvent(store.getState(), gcHobLandingScreen);
        setProvisionalResponse(provisionalResponse);
      } else {
        fetchAndSetProvisonalData(request);
      }
    } catch (e) {
      //
    }

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  useEffect(() => {
    if (destinationLocation?.address) {
      setInputValue(destinationLocation?.address);
    }

    if (sourceLocation?.address) {
      setSourceInput(sourceLocation?.address);
    }
  }, [destinationLocation, sourceLocation]);

  useEffect(() => {
    if (provisionalResponse?.default_pickup_time) {
      setMinimumPickupDate(new Date(provisionalResponse?.default_pickup_time));
      setTime(time || provResponse?.default_pickup_time);
    }
  }, [provisionalResponse?.default_pickup_time]);

  const formatTextWithBold = (text) => {
    const boldRegex = /\*([^*]+)\*/g;
    return text.split(boldRegex).map((part, index) => {
      if (index % 2 === 0) {
        return part;
      } else {
        return <strong key={index}>{part}</strong>;
      }
    });
  };

  const setTime = (time) => {
    dispatch({
      type: UPDATE_HOB_PICKUP_TIME,
      pickupTime: time,
    });
  };

  /**
   * Validates a provisional response and performs tracking based on the response data.
   * @param {object} provisionalResponse - The provisional response object containing response and error details.
   * @param {Array} provisionalResponse.response - Array containing response data.
   * @param {string} provisionalResponse.errors.error_list - List of errors in the response.
   * @returns {void}
   */
  const validateAndTrackeProvisionalResponse = (provisionalResponse) => {
    if (Boolean(provisionalResponse?.response?.length)) {
      pageViewOmniEvent(store.getState(), gcHobLandingScreen);
    } else {
      pushCtaOmniEvents(
        `${MOB_CABS_HOB_LANDING}_${DYNAMIC_ERROR}_${
          provisionalResponse?.errors?.error_list[0]?.code || ""
        }`,
        CTA_TYPE.HYPER_LINK,
        HOB_LANDING_PAGE,
        CABS_HOB_LANDING_ROUTE
      );
      sendEventOnError([
        {
          field: `hob_landing_load_error_${
            provisionalResponse?.errors?.error_list[0]?.code || ""
          }`,
          message: `${
            provisionalResponse?.errors?.error_list[0]?.message ?? ""
          }`,
        },
      ]);
      setErrScreenData(provisionalResponse?.errors?.error_list[0]);
    }
  };

  const handleDestinationEmpty = () => {
    pushCtaOmniEvents(
      `${MOB_CABS_HOB_LANDING}_${DESTINATION_NOT_ENTERED}`,
      CTA_TYPE.BUTTON,
      HOB_LANDING_PAGE,
      CABS_HOB_LANDING_ROUTE
    );
    showToast("Please enter your Destination");
  };

  const handleSourceEmpty = () => {
    pushCtaOmniEvents(
      `${MOB_CABS_HOB_LANDING}_${SOURCE_NOT_ENTERED}`,
      CTA_TYPE.BUTTON,
      HOB_LANDING_PAGE,
      CABS_HOB_LANDING_ROUTE
    );
    showToast("Please enter your Source");
  };

  const handleSourceDestinationSame = () => {
    pushCtaOmniEvents(
      `${MOB_CABS_HOB_LANDING}_${SOURCE_DESTINATION_SAME}`,
      CTA_TYPE.BUTTON,
      HOB_LANDING_PAGE,
      CABS_HOB_LANDING_ROUTE
    );
    showToast("Please enter different source and destination");
  };

  /**
   * Handles the click event for checking fare, triggering relevant tracking events and dispatchs,
   * @async
   * @function checkFareClicked
   * @returns {void}
   */
  const checkFareClicked = async () => {
    pushCtaOmniEvents(
      `${MOB_CABS_HOB_LANDING}_${CHECK_FARE_CLICKED}`,
      CTA_TYPE.BUTTON,
      HOB_LANDING_PAGE,
      CABS_HOB_LANDING_ROUTE
    );

    if (!flightId && !sourceLocation?.address) {
      handleSourceEmpty();
      return;
    }

    if (!destinationLocation?.address) {
      handleDestinationEmpty();
      return;
    }

    if (
      !flightId &&
      sourceLocation?.place_id === destinationLocation?.place_id
    ) {
      handleSourceDestinationSame();
      return;
    }

    navigateTo("listingHob");
  };

  const onBackPressed = () => {
    pushCtaOmniEvents(
      `${MOB_CABS_HOB_LANDING}_${CALENDAR_BACK_CLICKED}`,
      CTA_TYPE.BUTTON,
      HOB_LANDING_PAGE,
      CABS_HOB_LANDING_ROUTE
    );
    setShowDateTimeWidget(false);
  };

  const onDonePressed = () => {
    pushCtaOmniEvents(
      `${MOB_CABS_HOB_LANDING}_${CALENDAR_DONE_CLICKED}`,
      CTA_TYPE.BUTTON,
      HOB_LANDING_PAGE,
      CABS_HOB_LANDING_ROUTE
    );
    setShowDateTimeWidget(false);
  };

  const onEditClicked = () => {
    pushCtaOmniEvents(
      `${MOB_CABS_HOB_LANDING}_${DATE_EDIT_CLICKED}`,
      CTA_TYPE.BUTTON,
      HOB_LANDING_PAGE,
      CABS_HOB_LANDING_ROUTE
    );
    setTime(time || provResponse?.default_pickup_time);
    setShowDateTimeWidget(true);
  };

  const onEditSourceClick = () => {
    pushCtaOmniEvents(
      `${MOB_CABS_HOB_LANDING}_${EDIT_SOURCE_CLICKED}`,
      CTA_TYPE.BUTTON,
      HOB_LANDING_PAGE,
      CABS_HOB_LANDING_ROUTE
    );
    dispatch({
      type: UPDATE_USE_SOURCE_LOCATION,
      useSourceLocation: true,
      sourceLocation: provResponse?.source_location,
    });
  };

  if (provResponse && !Object.keys(provResponse)?.length) {
    return null;
  }

  if (errScreenData) {
    return (
      <ErrorScreen
        title={errScreenData?.title}
        message={errScreenData?.message || errScreenData?.code}
        icon={errIcon}
        cta={errActionCta}
      />
    );
  }

  return (
    <>
      {provResponse && Object.keys(provResponse)?.length ? (
        <S.BrandLpContainer>
          <S.BrandLpBgTop
            style={{
              backgroundImage: `url(${provResponse?.image})`,
            }}
          >
            <S.TerminalMarker>
              <S.PulsatingCircle>
                <S.InnerCircle>
                  <S.InnerPlus />
                </S.InnerCircle>
              </S.PulsatingCircle>
              {provResponse?.terminal ? (
                <Tooltip
                  text={provResponse?.terminal}
                  showTooltipAlways={provResponse?.terminal ? true : false}
                />
              ) : null}
            </S.TerminalMarker>
            <S.TerminalCabsContainer className={`${cabPosition || ""}`}>
              <Tooltip text={`${randomNumber} min`} showTooltipAlways={true} />
              <div className="cab-images">
                <S.TerminalCarImg className="cab1" src={HOB_CAR_IMG_URL} />
                <S.TerminalCarImg className="cab2" src={HOB_CAR_IMG_URL} />
                <S.TerminalCarImg className="cab3" src={HOB_CAR_IMG_URL} />
              </div>
            </S.TerminalCabsContainer>

            <S.BrandLpHeader>
              <S.MmtLogo src={GOIBIBO_ICON_URL} />
              <S.BrandMMTHeaderTitle>{GOIBIBO}</S.BrandMMTHeaderTitle>
            </S.BrandLpHeader>
          </S.BrandLpBgTop>
          <S.BottomFixedSection>
            <S.BrandLpBgBottom>
              <S.GetCabTitle>{provResponse?.title}</S.GetCabTitle>
              <S.GetBrandSubTitle>
                {provResponse?.description}
              </S.GetBrandSubTitle>
              <S.PartnerCabLogos>
                {smallCabLogos.map((img, i) => (
                  <S.SmallCabLogos src={img} key={"smallCabLogos" + i} />
                ))}
              </S.PartnerCabLogos>
            </S.BrandLpBgBottom>
            <S.SchedulingSection>
              <S.SchedulingSectionContainer>
                {provResponse?.airport_name && !useSourceLocation ? (
                  <>
                    <S.PickLabel>Pick up from:</S.PickLabel>
                    <S.PickupEditContainer>
                      <S.PickUpLocation>
                        {provResponse?.airport_name}
                      </S.PickUpLocation>
                      {provisionalResponse?.open_from_search ? (
                        <S.EditIcon
                          src={HOB_EDIT_FILL_ICON}
                          onClick={onEditSourceClick}
                        />
                      ) : null}
                    </S.PickupEditContainer>
                  </>
                ) : (
                  <S.InputLocationCont
                    showDisabled={!sourceLocation?.address}
                    onClick={() => {
                      setShowSrcPickerWidget(true);
                    }}
                  >
                    <img
                      src={
                        !sourceLocation?.address
                          ? HOB_FROM_IMG_URL_DISABLED
                          : HOB_FROM_IMG_URL
                      }
                    />
                    <div className="titleSubtitleCont">
                      <div className="title">FROM</div>
                      <div className="subtitle">{sourceInput}</div>
                    </div>
                  </S.InputLocationCont>
                )}
                <S.InputLocationCont
                  showDisabled={!destinationLocation?.address}
                  onClick={() => {
                    setShowDestPickerWidget(true);
                  }}
                >
                  <img
                    src={
                      !destinationLocation?.address
                        ? HOB_TO_IMG_URL_DISABLED
                        : HOB_TO_IMG_URL
                    }
                  />
                  <div className="titleSubtitleCont">
                    <div className="title">TO</div>
                    <div className="subtitle">{inputValue}</div>
                  </div>
                </S.InputLocationCont>
                <S.DateTimeWrapper>
                  <S.DateTimeSection>
                    <div className="date-time-container">
                      <S.DtLabel>Date & Time</S.DtLabel>
                      <S.DateNTime>
                        <S.RideTime>
                          {formatDate(
                            time ||
                              `${provisionalResponse?.default_pickup_time}`,
                            true
                          )}
                        </S.RideTime>
                        <S.Seperator>|</S.Seperator>
                        <S.RideTime>
                          {formatTime(
                            time ||
                              `${provisionalResponse?.default_pickup_time}`
                          )}
                        </S.RideTime>
                      </S.DateNTime>
                    </div>
                    <S.EditTimeContainer onClick={() => onEditClicked()}>
                      Edit Time{" "}
                      <img
                        className="down-arrow-date"
                        src={HOB_DOWN_ARROW_URL}
                      />
                    </S.EditTimeContainer>
                  </S.DateTimeSection>
                  {provResponse?.flight_info_text && (
                    <S.FlightTxtMsgContainer>
                      <img src={HOB_CHAT_ICON_URL} className="chatIc" />
                      <p>
                        {formatTextWithBold(provResponse?.flight_info_text)}
                      </p>
                    </S.FlightTxtMsgContainer>
                  )}
                </S.DateTimeWrapper>
              </S.SchedulingSectionContainer>
              <div>
                <S.BottomBrandBtn>
                  <Button
                    handleClick={() => checkFareClicked()}
                    customClass={`primaryCustBtn isGi`}
                    variant="primary"
                    content={`${provResponse?.check_fare_section?.btn_text}`}
                  />
                </S.BottomBrandBtn>
              </div>
            </S.SchedulingSection>
          </S.BottomFixedSection>
          {showDateTimeWidget && (
            <S.BottomSheetBackdrop
              isOpen={showDateTimeWidget}
              onClick={onBackPressed}
            >
              <S.BottomSheet
                isOpen={showDateTimeWidget}
                onClick={(e) => e.stopPropagation()}
              >
                <DateTimePicker
                  minStartDate={minimumPickupDate}
                  onBackClick={onBackPressed}
                  startDate={
                    new Date(time || provResponse?.default_pickup_time)
                  }
                  onFooterBtnClick={({ startDate }) => {
                    setTime(startDate.getTime());
                    onDonePressed();
                  }}
                />{" "}
              </S.BottomSheet>
            </S.BottomSheetBackdrop>
          )}
          {showDestPickerWidget || showSrcPickerWidget ? (
            <S.WidgetPopUp>
              <SrcDestPicker
                isPickup={showSrcPickerWidget}
                locationName={
                  showSrcPickerWidget
                    ? sourceLocation?.address
                    : destinationLocation?.address
                }
                onBackClick={() => {
                  setShowSrcPickerWidget(false);
                  setShowDestPickerWidget(false);
                }}
              />
            </S.WidgetPopUp>
          ) : null}
        </S.BrandLpContainer>
      ) : null}
    </>
  );
};

export default HouseOfBrands;
